/** 通用请求参数，在 fetch 基础上增加 JSON、超时和统一错误处理。 */
export interface ApiRequestOptions<TBody = unknown>
  extends Omit<RequestInit, 'body' | 'headers'> {
  body?: TBody;
  headers?: HeadersInit;
  timeout?: number;
}

/** 保存接口错误的 HTTP 状态、响应内容和建议重试时间。 */
export class ApiError<TData = unknown> extends Error {
  readonly status: number;
  readonly data?: TData;
  readonly retryAfter?: number;

  constructor(message: string, status: number, data?: TData, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.retryAfter = retryAfter;
  }
}

/** 尝试读取 JSON；服务端偶尔返回空内容时保持为 undefined。 */
const parseResponse = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return undefined;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError('接口返回的数据格式不正确。', response.status, text);
  }
};

/** 从 WordPress 或上游的错误数据中提取适合展示的消息。 */
const getErrorMessage = (data: unknown, status: number): string => {
  if (typeof data === 'object' && data !== null && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }

  return status === 429 ? '请求较频繁，请稍后再试。' : `请求失败（HTTP ${status}）。`;
};

/** 将 Retry-After 响应头转换成秒数，供界面展示等待提示。 */
const parseRetryAfter = (value: string | null): number | undefined => {
  if (!value) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds);

  const retryDate = Date.parse(value);
  return Number.isNaN(retryDate) ? undefined : Math.max(0, Math.ceil((retryDate - Date.now()) / 1000));
};

/** 兼容 WordPress 将建议等待秒数放在错误 data 中的情况。 */
const getRetryAfter = (response: Response, data: unknown): number | undefined => {
  const headerValue = parseRetryAfter(response.headers.get('Retry-After'));
  if (headerValue !== undefined) return headerValue;

  if (typeof data === 'object' && data !== null && 'data' in data) {
    const errorData = (data as { data?: unknown }).data;
    if (typeof errorData === 'object' && errorData !== null && 'retry_after' in errorData) {
      const seconds = Number((errorData as { retry_after?: unknown }).retry_after);
      if (Number.isFinite(seconds)) return Math.max(0, seconds);
    }
  }

  return undefined;
};

/**
 * 发起通用 HTTP 请求。
 * 默认按 JSON 发送和接收，并在超时或非 2xx 响应时抛出 ApiError。
 */
export const request = async <TResponse, TBody = unknown>(
  url: string,
  options: ApiRequestOptions<TBody> = {},
): Promise<TResponse> => {
  const {
    body,
    headers: customHeaders,
    timeout = 65_000,
    signal: externalSignal,
    ...fetchOptions
  } = options;
  const controller = new AbortController();
  const abortFromOutside = () => controller.abort(externalSignal?.reason);
  const timeoutId = window.setTimeout(() => controller.abort(), timeout);

  if (externalSignal) {
    if (externalSignal.aborted) abortFromOutside();
    else externalSignal.addEventListener('abort', abortFromOutside, { once: true });
  }

  const headers = new Headers(customHeaders);
  headers.set('Accept', 'application/json');
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: fetchOptions.credentials ?? 'same-origin',
      headers,
      signal: controller.signal,
    });
    const data = await parseResponse(response);

    if (!response.ok) {
      throw new ApiError(
        getErrorMessage(data, response.status),
        response.status,
        data,
        getRetryAfter(response, data),
      );
    }

    return data as TResponse;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(externalSignal?.aborted ? '请求已取消。' : '请求超时，请稍后再试。', 0);
    }
    throw new ApiError(error instanceof Error ? error.message : '网络请求失败。', 0);
  } finally {
    window.clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', abortFromOutside);
  }
};

/** 使用 POST 方法发送 JSON 数据。 */
export const post = <TResponse, TBody = unknown>(
  url: string,
  body: TBody,
  options: Omit<ApiRequestOptions<TBody>, 'body' | 'method'> = {},
): Promise<TResponse> => request<TResponse, TBody>(url, { ...options, method: 'POST', body });

/** 使用 GET 方法获取 JSON 数据。 */
export const get = <TResponse>(
  url: string,
  options: Omit<ApiRequestOptions<never>, 'body' | 'method'> = {},
): Promise<TResponse> => request<TResponse>(url, { ...options, method: 'GET' });
